/* =========================================================
   OREI PRODUCT GUIDE
   PERFORMANCE / SMOOTH FIRST-FLIP VERSION

   IMPORTANT:
   - No visible loading screen
   - Cover appears quickly
   - First spread is silently prepared
   - First flip waits until required pages are ready
   - Background rendering is throttled
   - No multiple heavy PDF renders fighting the flip animation
   - Existing zoom / overview / fullscreen / page numbers remain
   ========================================================= */

(function () {
  "use strict";


  /* =========================================================
     CONFIG
     ========================================================= */

  const PDF_WORKER =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";


  const EMPTY_IMAGE =
    "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";


  const MIN_ZOOM = 1;
  const MAX_ZOOM = 2;
  const ZOOM_STEP = 0.25;

  const THUMBNAIL_WIDTH = 130;

  const PAGES_PER_PDF = 10;

  const initializedSections =
    new WeakSet();



  /* =========================================================
     WAIT FOR PDF.JS
     ========================================================= */

  function waitForPdfJs(
    timeout = 15000
  ) {

    return new Promise(
      function (
        resolve,
        reject
      ) {

        const started =
          Date.now();


        function check() {

          if (
            typeof window.pdfjsLib !==
            "undefined"
          ) {

            resolve();

            return;

          }


          if (
            Date.now() -
            started >
            timeout
          ) {

            reject(
              new Error(
                "PDF.js failed to load."
              )
            );

            return;

          }


          window.setTimeout(
            check,
            50
          );

        }


        check();

      }
    );

  }



  /* =========================================================
     WAIT FOR STPAGEFLIP
     ========================================================= */

  function waitForPageFlip(
    timeout = 15000
  ) {

    return new Promise(
      function (
        resolve,
        reject
      ) {

        const started =
          Date.now();


        function check() {

          if (
            typeof window.St !==
              "undefined" &&
            typeof window.St.PageFlip ===
              "function"
          ) {

            resolve();

            return;

          }


          if (
            Date.now() -
            started >
            timeout
          ) {

            reject(
              new Error(
                "StPageFlip failed to load."
              )
            );

            return;

          }


          window.setTimeout(
            check,
            50
          );

        }


        check();

      }
    );

  }



  /* =========================================================
     BROWSER IDLE
     ========================================================= */

  function waitForBrowserIdle() {

    return new Promise(
      function (
        resolve
      ) {

        if (
          "requestIdleCallback"
          in window
        ) {

          window.requestIdleCallback(

            function () {

              resolve();

            },

            {
              timeout:
                800
            }

          );

        }

        else {

          window.setTimeout(
            resolve,
            100
          );

        }

      }
    );

  }



  /* =========================================================
     ERROR
     ========================================================= */

  function showError(
    section,
    error
  ) {

    console.error(
      "OREI Flipbook Error:",
      error
    );


    const errorBox =
      section.querySelector(
        ".orei-flipbook-error"
      );


    if (
      errorBox
    ) {

      errorBox.hidden =
        false;

    }

  }



  /* =========================================================
     INITIALIZE
     ========================================================= */

  async function initializeFlipbook(
    section
  ) {

    if (
      !section ||
      initializedSections.has(
        section
      )
    ) {

      return;

    }


    initializedSections.add(
      section
    );



    /* =======================================================
       PDF URLS
       ======================================================= */

    const pdfUrls = [

      section.dataset.pdfOne,
      section.dataset.pdfTwo,
      section.dataset.pdfThree,
      section.dataset.pdfFour

    ].filter(
      function (
        url
      ) {

        return (
          typeof url ===
            "string" &&
          url.trim() !==
            ""
        );

      }
    );



    if (
      pdfUrls.length !==
      4
    ) {

      showError(

        section,

        new Error(
          "Four catalog PDF URLs are required."
        )

      );

      return;

    }



    /* =======================================================
       ELEMENTS
       ======================================================= */

    const book =
      section.querySelector(
        ".orei-flipbook-book"
      );


    const bookArea =
      section.querySelector(
        ".orei-flipbook-book-area"
      );


    const zoomLayer =
      section.querySelector(
        ".orei-flipbook-zoom-layer"
      );


    const prevButton =
      section.querySelector(
        ".orei-flipbook-prev"
      );


    const nextButton =
      section.querySelector(
        ".orei-flipbook-next"
      );


    const pageNumber =
      section.querySelector(
        ".orei-flipbook-page-number"
      );


    const zoomInButton =
      section.querySelector(
        ".orei-zoom-in"
      );


    const zoomOutButton =
      section.querySelector(
        ".orei-zoom-out"
      );


    const zoomResetButton =
      section.querySelector(
        ".orei-zoom-reset"
      );


    const overviewButton =
      section.querySelector(
        ".orei-overview-button"
      );


    const overviewPanel =
      section.querySelector(
        ".orei-page-overview"
      );


    const overviewClose =
      section.querySelector(
        ".orei-overview-close"
      );


    const overviewList =
      section.querySelector(
        ".orei-overview-list"
      );


    const fullscreenButton =
      section.querySelector(
        ".orei-fullscreen-button"
      );



    if (
      !book ||
      !bookArea ||
      !zoomLayer
    ) {

      showError(

        section,

        new Error(
          "Flipbook container is missing."
        )

      );

      return;

    }



    try {


      /* =====================================================
         LOAD LIBRARIES
         ===================================================== */

      const pageFlipLibraryPromise =
        waitForPageFlip();


      await waitForPdfJs();


      window.pdfjsLib
        .GlobalWorkerOptions
        .workerSrc =
          PDF_WORKER;



      /* =====================================================
         PDF CACHE
         ===================================================== */

      const pdfDocuments =
        new Array(
          pdfUrls.length
        ).fill(
          null
        );


      const pdfPromises =
        new Array(
          pdfUrls.length
        ).fill(
          null
        );



      function loadPdfDocument(
        documentIndex
      ) {

        if (
          pdfDocuments[
            documentIndex
          ]
        ) {

          return Promise.resolve(

            pdfDocuments[
              documentIndex
            ]

          );

        }


        if (
          pdfPromises[
            documentIndex
          ]
        ) {

          return pdfPromises[
            documentIndex
          ];

        }


        const task =
          window.pdfjsLib
            .getDocument({

              url:
                pdfUrls[
                  documentIndex
                ]

            });


        pdfPromises[
          documentIndex
        ] =
          task.promise.then(
            function (
              pdf
            ) {

              pdfDocuments[
                documentIndex
              ] =
                pdf;


              return pdf;

            }
          );


        return pdfPromises[
          documentIndex
        ];

      }



      /* =====================================================
         LOAD ONLY PDF 1 AT START
         ===================================================== */

      const firstPdf =
        await loadPdfDocument(
          0
        );



      /* =====================================================
         PAGE RATIO
         ===================================================== */

      const firstPdfPage =
        await firstPdf.getPage(
          1
        );


      const firstViewport =
        firstPdfPage
          .getViewport({

            scale:
              1

          });


      const pageRatio =
        firstViewport.height /
        firstViewport.width;


      firstPdfPage.cleanup();



      /* =====================================================
         PAGE MAP
         ===================================================== */

      const totalPages =
        pdfUrls.length *
        PAGES_PER_PDF;


      const pageMap =
        [];


      for (
        let i = 0;
        i < totalPages;
        i++
      ) {

        pageMap.push({

          documentIndex:
            Math.floor(
              i /
              PAGES_PER_PDF
            ),

          pageNumber:
            (
              i %
              PAGES_PER_PDF
            ) +
            1

        });

      }



      /* =====================================================
         BUILD HTML PAGES
         ===================================================== */

      book.innerHTML =
        "";


      const pageImages =
        [];


      for (
        let i = 0;
        i < totalPages;
        i++
      ) {

        const page =
          document.createElement(
            "div"
          );


        page.className =
          "orei-flipbook-page";


        const image =
          document.createElement(
            "img"
          );


        image.src =
          EMPTY_IMAGE;


        image.alt =
          "OREI Product Catalog Page " +
          (
            i +
            1
          );


        image.draggable =
          false;


        page.appendChild(
          image
        );


        book.appendChild(
          page
        );


        pageImages.push(
          image
        );

      }



      /* =====================================================
         RENDER STORAGE
         ===================================================== */

      const renderedPages =
        new Set();


      const renderingPages =
        new Map();


      const pageObjectUrls =
        [];



      /* =====================================================
         RENDER ONE PAGE
         ===================================================== */

      async function renderPage(
        index
      ) {

        if (
          index < 0 ||
          index >=
            totalPages
        ) {

          return;

        }


        if (
          renderedPages.has(
            index
          )
        ) {

          return;

        }


        if (
          renderingPages.has(
            index
          )
        ) {

          return renderingPages.get(
            index
          );

        }



        const renderPromise =
          (async function () {


            const mapping =
              pageMap[
                index
              ];


            const pdf =
              await loadPdfDocument(
                mapping.documentIndex
              );


            const pdfPage =
              await pdf.getPage(
                mapping.pageNumber
              );


            const baseViewport =
              pdfPage.getViewport({

                scale:
                  1

              });



            const targetWidth =
              window.innerWidth <=
                749
                ? 900
                : 1600;



            const scale =
              targetWidth /
              baseViewport.width;



            const viewport =
              pdfPage.getViewport({

                scale:
                  scale

              });



            const canvas =
              document.createElement(
                "canvas"
              );


            canvas.width =
              Math.round(
                viewport.width
              );


            canvas.height =
              Math.round(
                viewport.height
              );



            const context =
              canvas.getContext(

                "2d",

                {
                  alpha:
                    false
                }

              );


            if (
              !context
            ) {

              throw new Error(
                "Could not render catalog page."
              );

            }



            context.fillStyle =
              "#ffffff";


            context.fillRect(

              0,
              0,

              canvas.width,
              canvas.height

            );



            await pdfPage.render({

              canvasContext:
                context,

              viewport:
                viewport

            }).promise;



            const blob =
              await new Promise(
                function (
                  resolve
                ) {

                  canvas.toBlob(

                    resolve,

                    "image/jpeg",

                    0.94

                  );

                }
              );


            if (
              !blob
            ) {

              throw new Error(
                "Could not create catalog page."
              );

            }



            const imageUrl =
              URL.createObjectURL(
                blob
              );


            pageObjectUrls.push(
              imageUrl
            );


            pageImages[
              index
            ].src =
              imageUrl;



            if (
              typeof pageImages[
                index
              ].decode ===
              "function"
            ) {

              try {

                await pageImages[
                  index
                ].decode();

              }

              catch (
                error
              ) {

                /* Ignore decode notification */

              }

            }



            renderedPages.add(
              index
            );


            canvas.width =
              1;


            canvas.height =
              1;


            pdfPage.cleanup();

          })();



        renderingPages.set(
          index,
          renderPromise
        );


        try {

          await renderPromise;

        }


        finally {

          renderingPages.delete(
            index
          );

        }

      }



      /* =====================================================
         CRITICAL STARTUP

         ONLY Page 1 is required to display the cover.
         ===================================================== */

      await renderPage(
        0
      );



      /* =====================================================
         WAIT FOR PAGEFLIP
         ===================================================== */

      await pageFlipLibraryPromise;



      /* =====================================================
         CREATE PAGEFLIP
         ===================================================== */

      const pageWidth =
        700;


      const pageHeight =
        Math.round(

          pageWidth *
          pageRatio

        );



      const pageFlip =
        new window.St.PageFlip(

          book,

          {

            width:
              pageWidth,


            height:
              pageHeight,


            size:
              "stretch",


            minWidth:
              280,


            maxWidth:
              850,


            minHeight:
              Math.round(
                280 *
                pageRatio
              ),


            maxHeight:
              Math.round(
                850 *
                pageRatio
              ),


            showCover:
              true,


            usePortrait:
              true,


            autoSize:
              true,


            drawShadow:
              true,


            maxShadowOpacity:
              0.35,


            flippingTime:
              700,


            useMouseEvents:
              true,


            swipeDistance:
              30,


            mobileScrollSupport:
              true,


            showPageCorners:
              true,


            disableFlipByClick:
              false

          }

        );



      pageFlip.loadFromHTML(

        book.querySelectorAll(
          ".orei-flipbook-page"
        )

      );



      /* =====================================================
         SHOW COVER
         ===================================================== */

      section.classList.add(
        "is-ready"
      );



      /* =====================================================
         SOFT COVER
         ===================================================== */

      const frontCover =
        pageFlip.getPage(
          0
        );


      function forceFrontCoverSoft() {

        if (
          !frontCover
        ) {

          return;

        }


        try {

          frontCover.setDensity(
            "soft"
          );


          frontCover.setDrawingDensity(
            "soft"
          );

        }

        catch (
          error
        ) {

          /* Ignore */

        }

      }


      forceFrontCoverSoft();



      /* =====================================================
         BACKGROUND RENDER QUEUE

         IMPORTANT FIX:
         Only ONE background page is rendered at a time.
         ===================================================== */

      const backgroundQueue =
        [];


      const backgroundQueued =
        new Set();


      let backgroundBusy =
        false;



      function addBackgroundPage(
        index
      ) {

        if (
          index < 0 ||
          index >=
            totalPages
        ) {

          return;

        }


        if (
          renderedPages.has(
            index
          ) ||
          renderingPages.has(
            index
          ) ||
          backgroundQueued.has(
            index
          )
        ) {

          return;

        }


        backgroundQueued.add(
          index
        );


        backgroundQueue.push(
          index
        );

      }



      async function processBackgroundQueue() {

        if (
          backgroundBusy
        ) {

          return;

        }


        backgroundBusy =
          true;


        try {

          while (
            backgroundQueue.length >
            0
          ) {

            const index =
              backgroundQueue.shift();


            backgroundQueued.delete(
              index
            );


            if (
              renderedPages.has(
                index
              )
            ) {

              continue;

            }


            /*
             * Wait until browser has breathing room.
             */

            await waitForBrowserIdle();


            try {

              await renderPage(
                index
              );

            }

            catch (
              error
            ) {

              console.error(
                "OREI background render:",
                error
              );

            }

          }

        }


        finally {

          backgroundBusy =
            false;

        }

      }



      function queueBackgroundPages(
        indexes
      ) {

        indexes.forEach(
          addBackgroundPage
        );


        processBackgroundQueue();

      }



      /* =====================================================
         FIRST-FLIP PREPARATION

         Pages 2, 3 and 4 are rendered silently BEFORE
         the first page-turn animation is allowed.

         The cover is already visible during this time.
         ===================================================== */

      let firstSpreadReady =
        false;


      /*
       * Prevent page-drag/corner interaction until the
       * first spread has been prepared.
       *
       * This avoids starting an animation while PDF.js
       * is still rasterizing Pages 2–3.
       */

      book.style.pointerEvents =
        "none";



      const firstSpreadReadyPromise =
        (async function () {

          try {

            /*
             * Sequential is intentional.
             *
             * Rendering these concurrently is what causes
             * the CPU spike / first-flip stutter.
             */

            await renderPage(
              1
            );


            await renderPage(
              2
            );


            await renderPage(
              3
            );


            firstSpreadReady =
              true;


            book.style.pointerEvents =
              "";


            /*
             * Only after the important opening pages
             * are finished do we start preparing later pages.
             */

            queueBackgroundPages([

              4,
              5,
              6,
              7,
              8,
              9

            ]);


          }

          catch (
            error
          ) {

            console.error(
              "OREI first spread preparation:",
              error
            );


            /*
             * Do not permanently block interaction.
             */

            firstSpreadReady =
              true;


            book.style.pointerEvents =
              "";

          }

        })();



      /* =====================================================
         PRIORITY PAGE PREPARATION

         Called before arrow navigation.
         ===================================================== */

      async function ensureForwardPagesReady(
        current
      ) {

        if (
          current === 0 &&
          !firstSpreadReady
        ) {

          await firstSpreadReadyPromise;

          return;

        }


        /*
         * Prepare pages which may become visible during
         * the next page turn.
         */

        const targets = [

          current + 1,
          current + 2,
          current + 3

        ];


        for (
          const index
          of targets
        ) {

          if (
            index >= 0 &&
            index <
              totalPages
          ) {

            await renderPage(
              index
            );

          }

        }

      }



      async function ensureBackwardPagesReady(
        current
      ) {

        const targets = [

          current - 1,
          current - 2

        ];


        for (
          const index
          of targets
        ) {

          if (
            index >= 0 &&
            index <
              totalPages
          ) {

            await renderPage(
              index
            );

          }

        }

      }



      /* =====================================================
         SMART BACKGROUND PRELOAD

         Pages closest to current position are queued first.
         ===================================================== */

      function preloadAround(
        currentIndex
      ) {

        queueBackgroundPages([

          currentIndex + 1,
          currentIndex + 2,
          currentIndex + 3,
          currentIndex + 4,
          currentIndex + 5,
          currentIndex + 6,

          currentIndex - 1,
          currentIndex - 2

        ]);

      }



      /* =====================================================
         PAGE RANGE
         ===================================================== */

      function getVisibleRange() {

        const current =
          pageFlip
            .getCurrentPageIndex();


        const orientation =
          pageFlip
            .getOrientation();


        if (
          current === 0
        ) {

          return {

            start:
              1,

            end:
              1

          };

        }


        const start =
          Math.min(

            current +
            1,

            totalPages

          );


        if (
          orientation ===
          "landscape"
        ) {

          return {

            start:
              start,

            end:
              Math.min(

                start +
                1,

                totalPages

              )

          };

        }


        return {

          start:
            start,

          end:
            start

        };

      }



      /* =====================================================
         PAGE NUMBER
         ===================================================== */

      function updatePageNumber() {

        if (
          !pageNumber
        ) {

          return;

        }


        const visible =
          getVisibleRange();


        if (
          visible.start ===
          visible.end
        ) {

          pageNumber.textContent =

            "Page " +
            visible.start +
            " / " +
            totalPages;

        }

        else {

          pageNumber.textContent =

            "Pages " +
            visible.start +
            "–" +
            visible.end +
            " / " +
            totalPages;

        }

      }



      /* =====================================================
         ZOOM
         ===================================================== */

      let zoomLevel =
        MIN_ZOOM;



      function getDisplayedZoomPercentage() {

        return Math.round(

          (
            (
              zoomLevel -
              MIN_ZOOM
            ) /

            (
              MAX_ZOOM -
              MIN_ZOOM
            )
          ) *

          100

        );

      }



      function centerZoomViewport() {

        if (
          zoomLevel <=
          MIN_ZOOM
        ) {

          bookArea.scrollLeft =
            0;


          bookArea.scrollTop =
            0;


          return;

        }


        window.requestAnimationFrame(
          function () {

            bookArea.scrollLeft =
              Math.max(

                0,

                (
                  bookArea.scrollWidth -
                  bookArea.clientWidth
                ) /
                2

              );


            bookArea.scrollTop =
              Math.max(

                0,

                (
                  bookArea.scrollHeight -
                  bookArea.clientHeight
                ) /
                2

              );

          }
        );

      }



      function applyZoom() {

        zoomLevel =
          Math.max(

            MIN_ZOOM,

            Math.min(

              MAX_ZOOM,

              zoomLevel

            )

          );


        zoomLayer.style.zoom =
          String(
            zoomLevel
          );


        bookArea.classList.toggle(

          "is-zoomed",

          zoomLevel >
            MIN_ZOOM

        );


        if (
          zoomResetButton
        ) {

          zoomResetButton.textContent =

            getDisplayedZoomPercentage() +
            "%";

        }


        if (
          zoomOutButton
        ) {

          zoomOutButton.disabled =

            zoomLevel <=
            MIN_ZOOM;

        }


        if (
          zoomInButton
        ) {

          zoomInButton.disabled =

            zoomLevel >=
            MAX_ZOOM;

        }


        centerZoomViewport();

      }



      zoomInButton
        ?.addEventListener(

          "click",

          function () {

            zoomLevel +=
              ZOOM_STEP;


            applyZoom();

          }

        );



      zoomOutButton
        ?.addEventListener(

          "click",

          function () {

            zoomLevel -=
              ZOOM_STEP;


            applyZoom();

          }

        );



      zoomResetButton
        ?.addEventListener(

          "click",

          function () {

            zoomLevel =
              MIN_ZOOM;


            applyZoom();

          }

        );


      applyZoom();



      /* =====================================================
         PAGE OVERVIEW
         ===================================================== */

      let overviewBuilt =
        false;


      let thumbnailObserver =
        null;


      const thumbnailUrls =
        [];


      const thumbnailPromises =
        new Map();



      async function renderThumbnail(
        catalogIndex,
        container
      ) {

        if (
          !container ||
          container.classList.contains(
            "is-loaded"
          )
        ) {

          return;

        }


        if (
          thumbnailPromises.has(
            catalogIndex
          )
        ) {

          await thumbnailPromises.get(
            catalogIndex
          );

          return;

        }



        const promise =
          (async function () {


            const mapping =
              pageMap[
                catalogIndex
              ];


            const pdf =
              await loadPdfDocument(
                mapping.documentIndex
              );


            const pdfPage =
              await pdf.getPage(
                mapping.pageNumber
              );


            const baseViewport =
              pdfPage.getViewport({

                scale:
                  1

              });


            const scale =
              THUMBNAIL_WIDTH /
              baseViewport.width;


            const viewport =
              pdfPage.getViewport({

                scale:
                  scale

              });


            const canvas =
              document.createElement(
                "canvas"
              );


            canvas.width =
              Math.round(
                viewport.width
              );


            canvas.height =
              Math.round(
                viewport.height
              );


            const context =
              canvas.getContext(

                "2d",

                {
                  alpha:
                    false
                }

              );


            if (
              !context
            ) {

              return;

            }


            context.fillStyle =
              "#ffffff";


            context.fillRect(

              0,
              0,

              canvas.width,
              canvas.height

            );


            await pdfPage.render({

              canvasContext:
                context,

              viewport:
                viewport

            }).promise;


            const blob =
              await new Promise(
                function (
                  resolve
                ) {

                  canvas.toBlob(

                    resolve,

                    "image/jpeg",

                    0.78

                  );

                }
              );


            if (
              !blob
            ) {

              return;

            }


            const url =
              URL.createObjectURL(
                blob
              );


            thumbnailUrls.push(
              url
            );


            const image =
              document.createElement(
                "img"
              );


            image.src =
              url;


            image.alt =

              "Catalog Page " +
              (
                catalogIndex +
                1
              );


            container.appendChild(
              image
            );


            container.classList.add(
              "is-loaded"
            );


            canvas.width =
              1;


            canvas.height =
              1;


            pdfPage.cleanup();

          })();


        thumbnailPromises.set(
          catalogIndex,
          promise
        );


        try {

          await promise;

        }


        finally {

          thumbnailPromises.delete(
            catalogIndex
          );

        }

      }



      function observeThumbnail(
        container
      ) {

        if (
          thumbnailObserver
        ) {

          thumbnailObserver.observe(
            container
          );

        }

        else {

          renderThumbnail(

            Number(
              container.dataset.pageIndex
            ),

            container

          );

        }

      }



      function createThumbnailObserver() {

        if (
          !overviewList ||
          !(
            "IntersectionObserver"
            in window
          )
        ) {

          return;

        }


        thumbnailObserver =
          new IntersectionObserver(

            function (
              entries
            ) {

              entries.forEach(
                function (
                  entry
                ) {

                  if (
                    !entry.isIntersecting
                  ) {

                    return;

                  }


                  const container =
                    entry.target;


                  renderThumbnail(

                    Number(
                      container.dataset.pageIndex
                    ),

                    container

                  );


                  thumbnailObserver.unobserve(
                    container
                  );

                }
              );

            },

            {

              root:
                overviewList,

              rootMargin:
                "250px 0px",

              threshold:
                0.01

            }

          );

      }



      function createOverviewCard(
        startPage,
        endPage
      ) {

        const button =
          document.createElement(
            "button"
          );


        button.type =
          "button";


        button.className =
          "orei-overview-spread";


        button.dataset.startPage =
          String(
            startPage
          );


        button.dataset.endPage =
          String(
            endPage
          );


        button.dataset.turnIndex =
          String(
            startPage -
            1
          );


        if (
          startPage ===
          endPage
        ) {

          button.classList.add(
            "is-single"
          );

        }



        const label =
          document.createElement(
            "span"
          );


        label.className =
          "orei-overview-label";


        label.textContent =

          startPage ===
            endPage

            ? "Page " +
              startPage

            : startPage +
              "–" +
              endPage;



        const row =
          document.createElement(
            "span"
          );


        row.className =
          "orei-overview-thumbnails";



        function addThumbnail(
          page
        ) {

          const thumb =
            document.createElement(
              "span"
            );


          thumb.className =
            "orei-overview-thumbnail";


          thumb.dataset.pageIndex =
            String(
              page -
              1
            );


          row.appendChild(
            thumb
          );


          observeThumbnail(
            thumb
          );

        }



        addThumbnail(
          startPage
        );


        if (
          endPage >
          startPage
        ) {

          addThumbnail(
            endPage
          );

        }



        button.appendChild(
          label
        );


        button.appendChild(
          row
        );



        button.addEventListener(

          "click",

          async function () {

            const pageIndex =
              Number(
                button.dataset.turnIndex
              );


            /*
             * Make target pages ready BEFORE jumping.
             */

            await renderPage(
              pageIndex
            );


            if (
              pageIndex + 1 <
              totalPages
            ) {

              await renderPage(
                pageIndex +
                1
              );

            }


            forceFrontCoverSoft();


            pageFlip.turnToPage(
              pageIndex
            );


            preloadAround(
              pageIndex
            );


            window.setTimeout(
              function () {

                forceFrontCoverSoft();

                updateEverything();

              },

              60
            );

          }

        );


        overviewList.appendChild(
          button
        );

      }



      function buildOverview() {

        if (
          overviewBuilt ||
          !overviewList
        ) {

          return;

        }


        overviewBuilt =
          true;


        createThumbnailObserver();


        createOverviewCard(
          1,
          1
        );


        for (
          let page =
            2;

          page <=
            totalPages;

          page +=
            2
        ) {

          createOverviewCard(

            page,

            Math.min(

              page +
              1,

              totalPages

            )

          );

        }

      }



      function updateOverviewHighlight() {

        if (
          !overviewBuilt ||
          !overviewList
        ) {

          return;

        }


        const visible =
          getVisibleRange();


        overviewList
          .querySelectorAll(
            ".orei-overview-spread"
          )
          .forEach(
            function (
              card
            ) {

              const start =
                Number(
                  card.dataset.startPage
                );


              const end =
                Number(
                  card.dataset.endPage
                );


              card.classList.toggle(

                "is-active",

                visible.start >=
                  start &&

                visible.start <=
                  end

              );

            }
          );

      }



      function openOverview() {

        if (
          !overviewPanel
        ) {

          return;

        }


        overviewPanel.hidden =
          false;


        overviewButton
          ?.setAttribute(
            "aria-expanded",
            "true"
          );


        buildOverview();

        updateOverviewHighlight();

      }



      function closeOverview() {

        if (
          !overviewPanel
        ) {

          return;

        }


        overviewPanel.hidden =
          true;


        overviewButton
          ?.setAttribute(
            "aria-expanded",
            "false"
          );

      }



      overviewButton
        ?.addEventListener(

          "click",

          function (
            event
          ) {

            event.stopPropagation();


            if (
              overviewPanel.hidden
            ) {

              openOverview();

            }

            else {

              closeOverview();

            }

          }

        );



      overviewClose
        ?.addEventListener(

          "click",

          closeOverview

        );



      /* =====================================================
         FULLSCREEN
         ===================================================== */

      fullscreenButton
        ?.addEventListener(

          "click",

          async function () {

            try {

              if (
                !document.fullscreenElement
              ) {

                await section
                  .requestFullscreen();

              }

              else {

                await document
                  .exitFullscreen();

              }

            }

            catch (
              error
            ) {

              console.warn(
                "OREI fullscreen:",
                error
              );

            }

          }

        );



      document.addEventListener(

        "fullscreenchange",

        function () {

          window.setTimeout(
            function () {

              const savedZoom =
                zoomLevel;


              zoomLayer.style.zoom =
                "1";


              try {

                pageFlip.update();

              }

              catch (
                error
              ) {

                /* Ignore */

              }


              zoomLevel =
                savedZoom;


              applyZoom();

              forceFrontCoverSoft();

              updateEverything();

            },

            160
          );

        }

      );



      /* =====================================================
         NAVIGATION
         ===================================================== */

      let locked =
        false;



      function updateButtons() {

        const current =
          pageFlip
            .getCurrentPageIndex();


        if (
          prevButton
        ) {

          prevButton.disabled =

            current <=
              0 ||

            locked;

        }


        if (
          nextButton
        ) {

          nextButton.disabled =

            current >=
              totalPages -
              1 ||

            locked;

        }

      }



      function updateEverything() {

        updateButtons();

        updatePageNumber();

        updateOverviewHighlight();

      }



      function unlock() {

        window.setTimeout(
          function () {

            locked =
              false;


            updateEverything();

          },

          720
        );

      }



      /* =====================================================
         PREVIOUS BUTTON

         Required pages are prepared BEFORE animation.
         ===================================================== */

      prevButton
        ?.addEventListener(

          "click",

          async function () {

            if (
              locked
            ) {

              return;

            }


            locked =
              true;


            updateEverything();


            const current =
              pageFlip
                .getCurrentPageIndex();


            /*
             * Never start the page animation while PDF.js
             * is still creating the pages involved.
             */

            await ensureBackwardPagesReady(
              current
            );


            forceFrontCoverSoft();


            pageFlip.flipPrev();


            preloadAround(
              Math.max(
                0,
                current -
                2
              )
            );


            unlock();

          }

        );



      /* =====================================================
         NEXT BUTTON

         THIS IS THE MAIN FIRST-FLIP FIX.
         ===================================================== */

      nextButton
        ?.addEventListener(

          "click",

          async function () {

            if (
              locked
            ) {

              return;

            }


            locked =
              true;


            updateEverything();


            const current =
              pageFlip
                .getCurrentPageIndex();


            /*
             * If visitor clicks immediately after the cover
             * appears, we wait silently for Pages 2–4.
             *
             * The page-turn animation itself does NOT begin
             * until those pages are fully rendered + decoded.
             *
             * Result:
             * no half-started / stuck / jerky first flip.
             */

            await ensureForwardPagesReady(
              current
            );


            forceFrontCoverSoft();


            pageFlip.flipNext();


            /*
             * Prepare the NEXT spread only AFTER this
             * critical work has finished.
             */

            preloadAround(
              current +
              2
            );


            unlock();

          }

        );



      /* =====================================================
         PAGE FLIP EVENT
         ===================================================== */

      pageFlip.on(

        "flip",

        function (
          event
        ) {

          const current =
            Number(
              event.data
            ) ||
            0;


          forceFrontCoverSoft();


          locked =
            false;


          updateEverything();


          /*
           * Queue future pages.
           * Rendering remains one-at-a-time during idle time.
           */

          preloadAround(
            current
          );

        }

      );



      /* =====================================================
         ORIENTATION
         ===================================================== */

      pageFlip.on(

        "changeOrientation",

        function () {

          forceFrontCoverSoft();

          updateEverything();

        }

      );



      /* =====================================================
         CLOSE OVERVIEW
         ===================================================== */

      document.addEventListener(

        "keydown",

        function (
          event
        ) {

          if (
            event.key ===
            "Escape"
          ) {

            closeOverview();

          }

        }

      );



      document.addEventListener(

        "click",

        function (
          event
        ) {

          if (
            !overviewPanel ||
            overviewPanel.hidden
          ) {

            return;

          }


          if (
            overviewPanel.contains(
              event.target
            )
          ) {

            return;

          }


          if (
            overviewButton
              ?.contains(
                event.target
              )
          ) {

            return;

          }


          closeOverview();

        }

      );



      /* =====================================================
         INITIAL UI
         ===================================================== */

      updateEverything();



      /* =====================================================
         CLEANUP
         ===================================================== */

      section._oreiFlipbookCleanup =
        function () {

          try {

            pageFlip.destroy();

          }

          catch (
            error
          ) {

            /* Ignore */

          }


          thumbnailObserver
            ?.disconnect();


          pageObjectUrls.forEach(
            function (
              url
            ) {

              URL.revokeObjectURL(
                url
              );

            }
          );


          thumbnailUrls.forEach(
            function (
              url
            ) {

              URL.revokeObjectURL(
                url
              );

            }
          );

        };


    }

    catch (
      error
    ) {

      showError(
        section,
        error
      );

    }

  }



  /* =========================================================
     INITIALIZE ALL
     ========================================================= */

  function initializeAll(
    root
  ) {

    const scope =
      root ||
      document;


    scope
      .querySelectorAll(
        "[data-orei-flipbook]"
      )
      .forEach(
        initializeFlipbook
      );

  }



  /* =========================================================
     NORMAL LOAD
     ========================================================= */

  if (
    document.readyState ===
    "loading"
  ) {

    document.addEventListener(

      "DOMContentLoaded",

      function () {

        initializeAll(
          document
        );

      }

    );

  }

  else {

    initializeAll(
      document
    );

  }



  /* =========================================================
     SHOPIFY THEME EDITOR
     ========================================================= */

  document.addEventListener(

    "shopify:section:load",

    function (
      event
    ) {

      initializeAll(
        event.target
      );

    }

  );


})();